import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XCircle, Trash2, RefreshCw, Download, Eye, FileDown } from 'lucide-react';
import { tasksApi, suppliersApi } from '../services/api';

const TasksPage = () => {
  const queryClient = useQueryClient();
  const [resultModal, setResultModal] = useState(null); // { taskId, taskName } или null
  const [resultData, setResultData] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const PAGE_SIZE = 50;

  const openResult = async (task) => {
    setResultModal({ taskId: task.task_id, taskName: task.task_name });
    setResultData(null);
    setResultLoading(true);
    try {
      const res = await tasksApi.getById(task.task_id);
      setResultData(res.data);
    } catch (e) {
      toast.error('Не удалось загрузить результат');
      setResultModal(null);
    } finally {
      setResultLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setResultModal(null);
    };
    if (resultModal) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [resultModal]);

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.getAll({ limit: 200 }).then(res => res.data),
    // Polling только если есть активные задачи (PENDING/STARTED/RETRY)
    // В react-query v5 функция получает query объект
    refetchInterval: (query) => {
      const data = query.state.data;
      const active = Array.isArray(data) && data.some(t => ['PENDING', 'STARTED', 'RETRY'].includes(t.status));
      return active ? 3000 : false;
    },
    staleTime: 10000,
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId) => tasksApi.cancel(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача отменена');
    },
    onError: () => toast.error('Ошибка отмены'),
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId) => tasksApi.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Задача удалена');
    },
    onError: () => toast.error('Ошибка удаления'),
  });

  const downloadJsonMutation = useMutation({
    mutationFn: (taskId) => {
      return tasksApi.downloadJson(taskId).then(response => {
        // Создаем blob и скачиваем файл
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `request_${taskId.substring(0, 8)}.json`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
    },
    onSuccess: () => toast.success('JSON скачан'),
    onError: () => toast.error('Ошибка скачивания JSON'),
  });

  const downloadSourceMutation = useMutation({
    mutationFn: async (task) => {
      // Извлекаем имя поставщика из названия задачи:
      // "Импорт файла поставщика: Орак" → "Орак"
      // "Импорт xls: Орак [file.xls]" → "Орак"
      let supplierId = null;

      const nameMatch = task.task_name?.match(/:\s*([^[\]]+?)(?:\s*\[|$)/);
      const supplierName = nameMatch ? nameMatch[1].trim() : null;

      if (supplierName) {
        try {
          const res = await suppliersApi.getAll({ limit: 1000 });
          const suppliers = res.data || [];
          const found = suppliers.find(s => s.name === supplierName);
          if (found) supplierId = found.id;
        } catch {}
      }

      // Fallback: попробовать взять из result задачи
      if (!supplierId) {
        try {
          const res = await tasksApi.getById(task.task_id);
          supplierId = res.data?.result?.supplier_id;
        } catch {}
      }

      if (!supplierId) throw new Error(`Не удалось определить поставщика для задачи "${task.task_name}"`);

      const response = await suppliersApi.downloadSourceFile(supplierId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data]);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^;\n]+)\1/);
      const filename = match ? match[2].trim() : `source_supplier_${supplierId}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Исходный файл скачан'),
    onError: (e) => toast.error(e.message || 'Ошибка скачивания исходника'),
  });

  const getStatusBadge = (status) => {
    const badges = {
      SUCCESS: 'badge-success',
      PENDING: 'badge-pending',
      STARTED: 'badge-info',
      FAILURE: 'badge-error',
      RETRY: 'badge-pending',
      REVOKED: 'badge-error',
    };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const getStatusText = (status) => {
    const texts = {
      SUCCESS: 'Успешно',
      PENDING: 'Ожидание',
      STARTED: 'Выполняется',
      FAILURE: 'Ошибка',
      RETRY: 'Повтор',
      REVOKED: 'Отменена',
    };
    return texts[status] || status;
  };

  if (isLoading) {
    return <div className="card">Загрузка...</div>;
  }

  const ERROR_STATUSES = ['FAILURE', 'REVOKED'];
  const filteredTasks = (tasks || []).filter(t => {
    if (statusFilter === 'errors') return ERROR_STATUSES.includes(t.status);
    if (statusFilter === 'active') return ['PENDING', 'STARTED', 'RETRY'].includes(t.status);
    return true;
  });
  const errorCount = (tasks || []).filter(t => ERROR_STATUSES.includes(t.status)).length;
  const activeCount = (tasks || []).filter(t => ['PENDING', 'STARTED', 'RETRY'].includes(t.status)).length;

  const totalTasks = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalTasks / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedTasks = filteredTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div>
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h1 style={{ fontSize: '24px', fontWeight: '600' }}>Очередь задач</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{(tasks || []).length} задач</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '5px 12px', fontSize: '13px' }}
                onClick={() => { setStatusFilter('all'); setPage(1); }}
              >
                Все
              </button>
              <button
                className={`btn ${statusFilter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '5px 12px', fontSize: '13px' }}
                onClick={() => { setStatusFilter('active'); setPage(1); }}
              >
                Активные {activeCount > 0 && <span style={{ background: '#3b82f6', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', marginLeft: '4px' }}>{activeCount}</span>}
              </button>
              <button
                className={`btn ${statusFilter === 'errors' ? 'btn-danger' : 'btn-secondary'}`}
                style={{ padding: '5px 12px', fontSize: '13px' }}
                onClick={() => { setStatusFilter('errors'); setPage(1); }}
              >
                Ошибки {errorCount > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', marginLeft: '4px' }}>{errorCount}</span>}
              </button>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => queryClient.invalidateQueries('tasks')}
            >
              <RefreshCw size={16} style={{ marginRight: '6px' }} />
              Обновить
            </button>
          </div>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Статус</th>
              <th>Создана</th>
              <th>Обновлена</th>
              <th>Ошибка</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {pagedTasks.map((task) => (
              <tr key={task.id}>
                <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                  {task.task_id.substring(0, 8)}...
                </td>
                <td>{task.task_name}</td>
                <td>
                  <span className={getStatusBadge(task.status)}>
                    {getStatusText(task.status)}
                  </span>
                </td>
                <td style={{ fontSize: '12px' }}>
                  {task.created_at ? new Date(task.created_at).toLocaleString('ru-RU') : '-'}
                </td>
                <td style={{ fontSize: '12px' }}>
                  {task.updated_at ? new Date(task.updated_at).toLocaleString('ru-RU') : '-'}
                </td>
                <td>
                  {task.error && (
                    <div style={{ color: '#ef4444', fontSize: '12px' }}>
                      {task.error}
                    </div>
                  )}
                </td>
                <td>
                  <div className="flex gap-2">
                    {task.status === 'STARTED' && (
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 12px' }}
                        onClick={() => cancelMutation.mutate(task.task_id)}
                      >
                        <XCircle size={16} />
                      </button>
                    )}
                    {task.status === 'SUCCESS' && (
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        title="Посмотреть результат"
                        onClick={() => openResult(task)}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    {task.task_name && task.task_name.toLowerCase().includes('отправка') && (
                      <button
                        className="btn btn-info"
                        style={{ padding: '6px 12px' }}
                        onClick={() => downloadJsonMutation.mutate(task.task_id)}
                        disabled={downloadJsonMutation.isPending}
                        title="Скачать JSON запроса"
                      >
                        <Download size={16} />
                      </button>
                    )}
                    {task.status === 'SUCCESS' &&
                      task.task_name &&
                      /импорт (xls|cloud|ftp|файла)/i.test(task.task_name) && (
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        onClick={() => downloadSourceMutation.mutate(task)}
                        disabled={downloadSourceMutation.isPending}
                        title="Скачать исходный файл поставщика"
                      >
                        <FileDown size={16} />
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 12px' }}
                      onClick={() => {
                        if (window.confirm('Удалить запись о задаче?')) {
                          deleteMutation.mutate(task.task_id);
                        }
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalTasks === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Задачи не найдены
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 10px' }}
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
            >«</button>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 10px' }}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === '...' ? (
                <span key={`dots-${i}`} style={{ padding: '4px 6px', color: '#6b7280' }}>…</span>
              ) : (
                <button
                  key={p}
                  className={`btn ${p === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '4px 10px', minWidth: '36px' }}
                  onClick={() => setPage(p)}
                >{p}</button>
              ))
            }
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 10px' }}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >›</button>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 10px' }}
              onClick={() => setPage(totalPages)}
              disabled={currentPage === totalPages}
            >»</button>
          </div>
        )}
      </div>

      {/* Модальное окно результата задачи */}
      {resultModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setResultModal(null)}>
          <div style={{
            background: '#fff', borderRadius: '8px', padding: '24px',
            width: '700px', maxWidth: '95vw', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', gap: '16px',
            overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{resultModal.taskName}</h2>
              <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => setResultModal(null)}>✕</button>
            </div>

            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>

            {resultLoading && <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Загрузка...</div>}

            {resultData && !resultLoading && (() => {
              const r = resultData.result || {};
              const notFound = r.not_found_items || [];
              const errorDetails = r.error_details || [];
              const problems = r.problems || [];
              const isNotify = problems.length > 0 || r.check_types !== undefined;
              const statKeys = ['problems_found', 'telegram_sent', 'checked_suppliers'];
              const stats = Object.entries(r).filter(([k]) =>
                !['not_found_items','error_details','message','success','problems','check_types','recipients'].includes(k)
                && (isNotify ? statKeys.includes(k) : true)
              );
              return (
                <>
                  {r.message && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 14px', color: '#166534', fontSize: '14px' }}>
                      {r.message}
                    </div>
                  )}
                  {resultData.error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', color: '#991b1b', fontSize: '14px' }}>
                      {resultData.error}
                    </div>
                  )}
                  {stats.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {stats.map(([key, val]) => {
                        const labels = { problems_found: 'Проблем', telegram_sent: 'Telegram отправлен', checked_suppliers: 'Проверено поставщиков' };
                        return (
                          <div key={key} style={{
                            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px',
                            padding: '8px 14px', minWidth: '120px'
                          }}>
                            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{labels[key] || key}</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                              {typeof val === 'boolean' ? (val ? '✅ Да' : '❌ Нет') : String(val)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {r.recipients && r.recipients.length > 0 && (
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      <span style={{ fontWeight: '600' }}>Получатели: </span>
                      {r.recipients.join(', ')}
                    </div>
                  )}
                  {r.check_types && r.check_types.length > 0 && (
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      <span style={{ fontWeight: '600' }}>Проверялось: </span>
                      {r.check_types.map(c => ({
                        no_update: 'ошибки обновления', zero_stock: 'нулевые остатки',
                        send_error: 'ошибки отправки', '1c_error': 'ошибки 1С', overdue: 'просроченный срок'
                      }[c] || c)).join(', ')}
                    </div>
                  )}
                  {problems.length > 0 && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '6px', color: '#b91c1c' }}>
                        Найденные проблемы ({problems.length}):
                      </div>
                      <div style={{
                        maxHeight: '320px', overflowY: 'auto',
                        border: '1px solid #fecaca', borderRadius: '6px',
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#fef2f2' }}>
                              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #fecaca', width: '25%' }}>Поставщик</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #fecaca', width: '30%' }}>Проблема</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #fecaca' }}>Детали</th>
                            </tr>
                          </thead>
                          <tbody>
                            {problems.map((p, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fef9f9', borderBottom: '1px solid #fee2e2' }}>
                                <td style={{ padding: '6px 10px', fontWeight: '500' }}>{p.supplier}</td>
                                <td style={{ padding: '6px 10px', color: '#b91c1c' }}>{p.check_label}</td>
                                <td style={{ padding: '6px 10px', color: '#374151' }}>{p.detail}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {isNotify && problems.length === 0 && !r.message && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 14px', color: '#166534', fontSize: '14px' }}>
                      ✅ Проблем не обнаружено
                    </div>
                  )}
                  {notFound.length > 0 && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '6px', color: '#92400e' }}>
                        Не найдено ({notFound.length}):
                      </div>
                      <div style={{
                        maxHeight: '280px', overflowY: 'auto',
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: '6px', padding: '10px'
                      }}>
                        {notFound.map((item, i) => (
                          <div key={i} style={{ fontSize: '12px', padding: '2px 0', borderBottom: i < notFound.length - 1 ? '1px solid #fde68a' : 'none' }}>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {errorDetails.length > 0 && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '6px', color: '#991b1b' }}>
                        Ошибки ({errorDetails.length}):
                      </div>
                      <div style={{
                        maxHeight: '200px', overflowY: 'auto',
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: '6px', padding: '10px'
                      }}>
                        {errorDetails.map((item, i) => (
                          <div key={i} style={{ fontSize: '12px', padding: '2px 0' }}>{item}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            </div>{/* scroll container */}
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
